import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Edit, Info, UserPlus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { Can } from '@/components/features/permission/can';
import { ScheduleButtons } from '@/components/features/schedule/buttons';
import { AddScheduleMemberDialog } from '@/components/features/schedule/dialogs/add-schedule-member-dialog/index';
import { ScheduleDaysBadges } from '@/components/features/schedule/schedule-days-badges';
import { ScheduleMembersList } from '@/components/features/schedule/schedule-member-list';
import ScheduleMembersLimit from '@/components/features/schedule/schedule-members-limit';
import { ScheduleModeBadge } from '@/components/features/schedule/schedule-mode-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent } from '@/components/ui/tabs';
import { useFormatDate } from '@/hooks/use-format-date';
import { getScheduleQuery } from '@/queries/getScheduleQuery';

export const Route = createFileRoute(
  '/_authenticated/schedule/$scheduleId/details'
)({
  component: RouteComponent,
});

function RouteComponent() {
  const organization = useCurrentOrganization();
  const { scheduleId } = Route.useParams();
  const { formatWithPreset, formatDistanceToNow } = useFormatDate();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: schedule } = useSuspenseQuery({
    ...getScheduleQuery(organization.organizationId, scheduleId),
  });

  if (!schedule) {
    return null;
  }

  return (
    <TabsContent value="details" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Schedule Information Card */}
        <Card className="md:col-span-12 border-blue-200/30 bg-blue-50/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <Info className="mr-2 h-5 w-5 text-blue-500" />
              {t('authenticated.schedule.details.details.schedule')}
            </CardTitle>
            <div className="flex space-x-2">
              <Can permissions={['schedule:update']}>
                <Button
                  onClick={() =>
                    navigate({
                      to: '/schedule/$scheduleId/edit',
                      params: { scheduleId },
                    })
                  }
                  icon={<Edit />}
                >
                  {t('authenticated.schedule.details.details.edit')}
                </Button>
              </Can>
              <ScheduleButtons.Delete
                organizationId={organization.organizationId}
                scheduleId={scheduleId}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column - Basic details */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {t('authenticated.schedule.details.details.scheduleMode')}
                  </p>
                  <ScheduleModeBadge mode={schedule.mode} />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {t('authenticated.schedule.details.details.created')}
                  </p>
                  <p>
                    {formatDistanceToNow(schedule.createdAt, {
                      addSuffix: true,
                    })}
                  </p>
                </div>
              </div>

              {/* Right column - Time information */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {t('authenticated.schedule.details.details.startDate')}
                  </p>
                  <p>
                    {formatWithPreset(schedule.startDate, 'day_month_year')}
                  </p>
                </div>
                {schedule.endDate && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {t('authenticated.schedule.details.details.endDate')}
                    </p>
                    <p>
                      {formatWithPreset(schedule.endDate, 'day_month_year')}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {t('authenticated.schedule.details.details.activeDays')}
                  </p>
                  <ScheduleDaysBadges days={schedule.daysOfWeek} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members Card */}
        <ScheduleMembersLimit
          organizationId={organization.organizationId}
          scheduleId={scheduleId}
        >
          {(limitExceeded, remaining) => (
            <Card className="md:col-span-12">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center">
                  <Users className="mr-2 h-5 w-5" />
                  {t(
                    'authenticated.schedule.details.details.scheduleMembers.title'
                  )}
                  <ScheduleMembersLimit.Badge className="ml-3" />
                </CardTitle>

                <Can permissions={['schedule:member:add']}>
                  {limitExceeded && (
                    <Button disabled icon={<UserPlus />}>
                      {t(
                        'authenticated.schedule.details.details.scheduleMembers.addMember'
                      )}
                    </Button>
                  )}

                  {!limitExceeded && (
                    <AddScheduleMemberDialog
                      maxMembersToAdd={remaining}
                      organizationId={organization.organizationId}
                      scheduleId={scheduleId}
                      trigger={
                        <Button icon={<UserPlus />}>
                          {t(
                            'authenticated.schedule.details.details.scheduleMembers.addMember'
                          )}
                        </Button>
                      }
                    />
                  )}
                </Can>
              </CardHeader>

              <CardContent>
                <ScheduleMembersList
                  scheduleId={scheduleId}
                  organizationId={organization.organizationId}
                />
              </CardContent>
            </Card>
          )}
        </ScheduleMembersLimit>
      </div>
    </TabsContent>
  );
}
